// Hairline icon set: 16px grid, 1.25px non-scaling stroke (base CSS), round caps/joins, currentColor.
//   <Icon name size? label? {...svg attrs} />   decorative (aria-hidden) unless `label` is given
//   iconNames: every IconName, in set order
// Status has no icons here: use <Verdict> and <Sev>. Never put an icon in a tinted square.
import type { SVGProps } from "react";
import { cx } from "./cx";

const S = { fill: "none", stroke: "currentColor", strokeWidth: 1.25, strokeLinecap: "round", strokeLinejoin: "round" } as const;
const F = { fill: "currentColor", stroke: "none" } as const;

const glyphs = {
  sun: <g {...S}><circle cx="8" cy="8" r="2.75" /><path d="M8 1.75v1.5M8 12.75v1.5M1.75 8h1.5M12.75 8h1.5M3.58 3.58l1.06 1.06M11.36 11.36l1.06 1.06M3.58 12.42l1.06-1.06M11.36 4.64l1.06-1.06" /></g>,
  moon: <g {...S}><path d="M13.25 9.6A5.5 5.5 0 1 1 6.4 2.75a4.4 4.4 0 0 0 6.85 6.85z" /></g>,
  submit: <g {...S}><path d="M8 10.25V2.5M5 5.5l3-3 3 3M2.5 10.5v3h11v-3" /></g>,
  link: <g {...S}><path d="M6.5 9.5l3-3M7.25 4.25l.9-.9a2.8 2.8 0 0 1 3.96 3.96l-.9.9M8.75 11.75l-.9.9a2.8 2.8 0 0 1-3.96-3.96l.9-.9" /></g>,
  file: <g {...S}><path d="M9.25 1.75H3.5v12.5h9V4.75zM9.25 1.75v3h3.25" /></g>,
  process: <g {...S}><rect x="1.75" y="2.75" width="12.5" height="10.5" rx="1" /><path d="M4.5 6.25l2 1.75-2 1.75M8.5 10.25h3" /></g>,
  network: <g {...S}><circle cx="3.75" cy="4.5" r="1.5" /><circle cx="12.25" cy="4.5" r="1.5" /><circle cx="8" cy="12" r="1.5" /><path d="M5.25 4.5h5.5M4.6 5.8l2.65 4.9M11.4 5.8l-2.65 4.9" /></g>,
  registry: <g {...S}><path d="M3.5 2v10.25M3.5 5.5h4.25M3.5 10.75h4.25" /><rect x="8.25" y="4" width="3.5" height="3" rx=".5" /><rect x="8.25" y="9.25" width="3.5" height="3" rx=".5" /></g>,
  memory: <g {...S}><rect x="4" y="4" width="8" height="8" rx=".75" /><path d="M6.25 1.75V4M9.75 1.75V4M6.25 12v2.25M9.75 12v2.25M1.75 6.25H4M1.75 9.75H4M12 6.25h2.25M12 9.75h2.25" /></g>,
  inject: <g {...S}><rect x="6.75" y="6.75" width="7.5" height="7.5" rx=".75" /><path d="M1.75 1.75l7 7M8.75 4.25v4.5h-4.5" /></g>,
  detection: <g {...S}><circle cx="8" cy="8" r="4.75" /><path d="M8 1.25v2.5M8 12.25v2.5M1.25 8h2.5M12.25 8h2.5" /><circle cx="8" cy="8" r=".9" {...F} /></g>,
  copy: <g {...S}><rect x="5.75" y="5.75" width="8.5" height="8.5" rx="1" /><path d="M10.25 5.75V2.75a1 1 0 0 0-1-1h-6.5a1 1 0 0 0-1 1v6.5a1 1 0 0 0 1 1h3" /></g>,
  download: <g {...S}><path d="M8 2.25v8.25M4.75 7.25L8 10.5l3.25-3.25M2.5 13.75h11" /></g>,
  refresh: <g {...S}><path d="M13.25 8A5.25 5.25 0 1 1 11.6 4.15" /><path d="M13.25 2.25v3.5h-3.5" /></g>,
  external: <g {...S}><path d="M9.5 2.25h4.25V6.5M13.75 2.25L7.5 8.5M11.75 9.5v4.25h-9.5v-9.5H6.5" /></g>,
  stop: <rect x="4" y="4" width="8" height="8" rx="1" {...S} />,
  live: <g><circle cx="8" cy="8" r="3" {...F} /><circle cx="8" cy="8" r="6" {...S} opacity=".35" /></g>,
  search: <g {...S}><circle cx="7" cy="7" r="4.5" /><path d="M10.4 10.4l3.85 3.85" /></g>,
  filter: <g {...S}><path d="M2 2.75h12L9.5 8.25v4.5l-3 1.5v-6z" /></g>,
  chevron: <g {...S}><path d="M4 6l4 4 4-4" /></g>,
  node: <g {...S}><rect x="2.25" y="2.25" width="11.5" height="4.75" rx=".75" /><rect x="2.25" y="9" width="11.5" height="4.75" rx=".75" /><path d="M8.75 4.6h2.75M8.75 11.4h2.75" /><circle cx="4.9" cy="4.6" r=".75" {...F} /><circle cx="4.9" cy="11.4" r=".75" {...F} /></g>,
  screen: <g {...S}><rect x="1.75" y="2.25" width="12.5" height="9" rx="1" /><path d="M5.5 14h5M8 11.25V14" /></g>,
  clock: <g {...S}><circle cx="8" cy="8" r="6.25" /><path d="M8 4.5V8l2.5 1.5" /></g>,
  pin: <g {...S}><circle cx="8" cy="4.5" r="2.5" /><path d="M8 7v7.25" /></g>,
  close: <g {...S}><path d="M4 4l8 8M12 4l-8 8" /></g>,
  github: <path {...F} d="M8 0c4.42 0 8 3.58 8 8a8.013 8.013 0 0 1-5.45 7.59c-.4.08-.55-.17-.55-.38 0-.27.01-1.13.01-2.2 0-.75-.25-1.23-.54-1.48 1.78-.2 3.65-.88 3.65-3.95 0-.88-.31-1.59-.82-2.15.08-.2.36-1.02-.08-2.12 0 0-.67-.22-2.2.82-.64-.18-1.32-.27-2-.27-.68 0-1.36.09-2 .27-1.53-1.03-2.2-.82-2.2-.82-.44 1.1-.16 1.92-.08 2.12-.51.56-.82 1.28-.82 2.15 0 3.06 1.86 3.75 3.64 3.95-.23.2-.44.55-.51 1.07-.46.21-1.61.55-2.33-.66-.15-.24-.6-.83-1.23-.82-.67.01-.27.38.01.53.34.19.73.9.82 1.13.16.45.68 1.31 2.69.94 0 .67.01 1.3.01 1.49 0 .21-.15.45-.55.38A7.995 7.995 0 0 1 0 8c0-4.42 3.58-8 8-8Z" />,
} as const;

export type IconName = keyof typeof glyphs;
export const iconNames = Object.keys(glyphs) as IconName[];

type Props = Omit<SVGProps<SVGSVGElement>, "children"> & { name: IconName; size?: number; label?: string };

export function Icon({ name, size = 16, label, className, ...rest }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden={label ? undefined : true}
      role={label ? "img" : undefined}
      aria-label={label}
      focusable="false"
      data-icon=""
      className={cx("shrink-0", className)}
      {...rest}
    >
      {glyphs[name]}
    </svg>
  );
}
